import { CalVial, CalVialLevel } from "../models/cal-lot.model";
import { CalibratorAssay, CalibratorSample, RunStatus } from "../models/result.model";

/**
 * Extracts the calibrator vial number encoded in the final two digits of an accession number.
 *
 * @param accessionNo - The calibrator sample accession number (e.g. `"...01"`).
 * @returns The vial number as an integer, or `undefined` if it cannot be parsed.
 */
function getVialNumberFromAccession(accessionNo: string | undefined): number | undefined {
  if (!accessionNo) {
    return undefined;
  }
  const parsed = parseInt(accessionNo.slice(-2), 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Extracts calibrator sample data for a specific region from vial information and run status.
 *
 * This function searches through each vial and its levels, matches the region,
 * and combines it with matching sample data from the run status to return structured calibrator sample entries.
 *
 * @param targetRegion - The region ID to match against vial levels.
 * @param runStatus - The run status object containing calibrator sample data.
 * @param vials - Array of `CalVial` objects containing barcode and level info.
 * @returns An array of `CalibratorSample` objects populated with concentration and statistical data for the region.
 */
export function getConcentrationByRegion(targetRegion: number, runStatus: RunStatus | undefined, vials: CalVial[] | undefined): CalibratorSample[] {
  const results: CalibratorSample[] = [];
  const calSamples = runStatus?.calCurveInformation?.calSamples;

  vials?.forEach((vial: CalVial, index: number) => {
    vial.levels.forEach((level: CalVialLevel) => {
      if (level.region === targetRegion) {
        const vialNumber = index + 1;
        // Match a calibrator sample to its vial by the vial number encoded in the
        // final two digits of accessionNo, rather than by positional order. Rows with
        // no matching sample are left unpopulated (rendered as a dash).
        const sampleData = calSamples?.find(sample => getVialNumberFromAccession(sample.accessionNo) === vialNumber);

        results.push({
          index: vialNumber,
          barcode: vial.barcode,
          //concentration: level.concentration,
          concentration: typeof level.concentration === 'string' ? parseFloat(level.concentration) === 0 ? 0 : parseFloat(level.concentration) : level.concentration === 0 ? 0 : level.concentration,
          meanRfi: sampleData?.meanRfi,
          meanFR: sampleData?.meanFR,
          adjustedFr: sampleData?.adjustedFr,
          repRfiCV: sampleData?.repRfiCV,
          repRfiCvOk: sampleData?.repRfiCvOk,
          weight: sampleData?.weight,
          repCount: sampleData?.repCount,
          repCountOk: sampleData?.repCountOk,
          meanNormalizingSignal: sampleData?.meanNormalizingSignal,
          repRfiSD: sampleData?.repRfiSD,
          meanBeadCount: sampleData?.meanBeadCount,
          meanBeadtoBeadCv: sampleData?.meanBeadtoBeadCv,
          meanCountTime: sampleData?.meanCountTime,
          repFrSD: sampleData?.repFrSD,
          repFrCV: sampleData?.repFrCV,
          repFrCvOk: sampleData?.repFrCvOk,
          levelNumber: sampleData?.levelNumber,
          originalAssignment: sampleData?.originalAssignment,
          adjustedAssignment: sampleData?.adjustedAssignment,
          optimizedAssignment: sampleData?.optimizedAssignment,
          accessionNo: sampleData?.accessionNo,
          calLevelCvSpec: sampleData?.calLevelCvSpec
        });
      }
    });
  });

  return results;
}

/**
 * Formats and normalizes calibrator assay data into a consistent row-based structure.
 *
 * Ensures that the calibrator data for each assay has exactly 8 rows (indexed 1–8),
 * filling missing entries with `undefined` to maintain UI or table consistency.
 *
 * @param calResponseCombinedData - Array of `CalibratorAssay` items, each containing a `calibratorData` array.
 * @returns A new array of `CalibratorAssay` items, each with a `fullCalibratorData` property that contains 8 entries.
 */
export function formatCalibratorDataSequential(calResponseCombinedData: CalibratorAssay[]) {
  const noOfRows: number[] = [1, 2, 3, 4, 5, 6, 7, 8];
  calResponseCombinedData = calResponseCombinedData?.map(item => {
    const availableData: CalibratorSample[] = item.calibratorData ?? []; // assumes sorted by index (can sort if not)
    const fullCalibratorData = [];

    for (let i = 0; i < noOfRows.length; i++) {
      const data = availableData[i] || undefined;
      fullCalibratorData.push({
        id: noOfRows[i],
        data: data
      });
    }

    return {
      ...item,
      fullCalibratorData
    };
  });

  return calResponseCombinedData;
}
